import React, {useCallback, useEffect, useState} from 'react';
import {useTitle} from "../../hooks/useTitle";
import Loading from "../../components/Loading";
import {Alert, Button, Col, Form, Modal, Row, Table} from "react-bootstrap";
import {FiPlus} from "react-icons/fi";
import {FaRegTrashCan} from "react-icons/fa6";
import {FaExpandAlt, FaRegEdit} from "react-icons/fa";
import ObjectService from "../../http/ObjectService";
import {useAuth} from "../../store/AuthContext";
import CategoriesService from "../../http/CategoriesService";
import ObjectModalView from "../../components/modals/ObjectModalView";
import ObjectModalDelete from "../../components/modals/ObjectModalDelete";
import ObjectModalEdite from "../../components/modals/ObjectModalEdite";

const UserObjects = () => {
    useTitle("Объекты");
    const {user, isAuth, isLoading} = useAuth();

    const [loading, setLoading] = useState<boolean>(true);
    const [objects, setObjects] = useState<any[] | null>(null);
    const [objectId, setObjectId] = useState<number | string | null>(null);
    const [categories, setCategories] = useState<any[] | null>(null);
    const [categoryId, setCategoryId] = useState<number | string | "">("");

    const [error, setError] = useState<string | null>(null);
    const [errorFiles, setErrorFiles] = useState<string | null>(null);
    const [errorForm, setErrorForm] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const [district, setDistrict] = useState<string>("");
    const [address, setAddress] = useState<string>("");
    const [name, setName] = useState<string>("");
    const [comment, setComment] = useState<string>("");
    const [images, setImages] = useState<any[]>([]);
    const [imagesInfo, setImagesInfo] = useState<any[]>([]);

    const [fullscreen, setFullscreen] = useState<boolean>(false);
    const [showCreate, setShowCreate] = useState(false);
    const [showView, setShowView] = useState(false);
    const [showEdite, setShowEdite] = useState(false);
    const [showDelete, setShowDelete] = useState(false);

    const [validated, setValidated] = useState(false);

    const fetchObjects = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Параллельная загрузка объектов и категорий
            const [objectsRes, categoriesRes] = await Promise.all([
                ObjectService.query({
                    includes: ["categories"],
                    options: { where: { action: null }, order: [['id', 'ASC']] }
                }),
                CategoriesService.query({
                    options: { where: { action: null }, order: [['id', 'DESC']] }
                })
            ]);
            setObjects(objectsRes.data);
            setCategories(categoriesRes.data);
        } catch (err: any) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            if (isLoading || !isAuth) return;
            void fetchObjects();
        };

        void fetchData();
    }, [fetchObjects, isAuth, isLoading]);

    const handleSubmitCreate = async (event: React.FormEvent<HTMLFormElement>) => {
        const form = event.currentTarget;
        event.preventDefault();

        if (!form.checkValidity()) {
            event.stopPropagation();
            setValidated(true);
            return;
        }

        const formData = new FormData();
        formData.append('district', district);
        formData.append('address', address);
        formData.append('name', name);
        formData.append('comment', comment);
        formData.append('userId', user.id);
        formData.append('categoryId', String(categoryId));
        formData.append('imagesInfo', JSON.stringify(imagesInfo));
        images.forEach(image => formData.append('images', image));

        try {
            await ObjectService.create(formData);
            setSuccess("Объект добавлен");
            setShowCreate(false);
            setValidated(false);
            await fetchObjects();
        } catch (err: any) {
            setErrorForm(err.response?.data?.message || err.message);
            setErrorFiles(null);
        }
    };

    const selectFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        setErrorFiles(null);
        const targetFiles = event.target.files;
        if (!targetFiles) return;

        if (targetFiles.length > 10) {
            setErrorFiles("Можно загрузить максимум 10 фотографий");
            setErrorForm(null);
        } else {
            const filesArray = Array.from(targetFiles);
            setImages(filesArray);
            setImagesInfo([]);

            filesArray.forEach(file => {
                const reader = new FileReader();
                reader.onload = (e: any) => {
                    const img = new Image();
                    img.onload = () => {
                        setImagesInfo(prev => [...prev, {
                            name: file.name,
                            width: img.width,
                            height: img.height,
                        }]);
                    };
                    img.src = e.target.result;
                };
                reader.readAsDataURL(file);
            });
        }
    };

    if (isLoading || loading) {
        return <Loading />;
    }

    return (
        <>
            <div className={"d-flex jc-between mb-3"}>
                <h4 className={"mb-0"}>Объекты</h4>
                <div>
                    <Button variant={"primary"} onClick={() => {
                        setShowCreate(true);
                        setErrorFiles(null);
                        setErrorForm(null);
                        setValidated(false);
                        setDistrict("");
                        setAddress("");
                        setName("");
                        setComment("");
                        setCategoryId("");
                        setImages([]);
                        setImagesInfo([]);
                    }}>
                        <FiPlus className={"me-1"}/>Добавить
                    </Button>
                </div>
            </div>


            {error && <Alert variant={"danger"} dismissible={true}>{error}</Alert>}
            {success && <Alert variant={"success"} dismissible={true}>{success}</Alert>}
            {objects && objects.length > 0
                ?
                <div className={"table-body scrollarea"}>
                    <Table bordered={true} hover={true} size="sm" className={"full"}>
                        <thead>
                        <tr>
                            <th style={{width: "1%"}}>ID</th>
                            <th>Название</th>
                            <th>Категория</th>
                            <th>Район</th>
                            <th>Адрес</th>
                            <th>Комментарий</th>
                            <th style={{width: "145px"}}>Создан</th>
                            <th style={{width: "60px"}}></th>
                        </tr>
                        </thead>
                        <tbody>

                        {objects.map((object: any) => {
                            return (
                                <tr key={object.id}>
                                    <td>{object.id}</td>
                                    <td>
                                        <Button variant={"link"} className={"lh-base"} onClick={() => {
                                            setObjectId(object.id);
                                            setShowView(true);
                                        }}>{object.name}
                                        </Button>
                                    </td>
                                    <td>{object?.category?.name}</td>
                                    <td>{object.district}</td>
                                    <td>{object.address}</td>
                                    <td>{object.comment}</td>
                                    <td>{new Date(object.createdAt).toLocaleString()}</td>
                                    <td>
                                        {object.userId === user.id && (
                                            <div className={"d-flex ai-center gap-3"}>
                                                <FaRegEdit className={"pointer text-primary"} onClick={() => {
                                                    setObjectId(object.id);
                                                    setShowEdite(true);
                                                }}/>
                                                <FaRegTrashCan className={"pointer text-danger"} onClick={() => {
                                                    setObjectId(object.id);
                                                    setShowDelete(true);
                                                }}/>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </Table>
                </div>
                :
                <Alert variant={"secondary"}>Нет объектов</Alert>
            }

            {showCreate && (
                <Modal show={showCreate} size={"xl"} fullscreen={fullscreen ? true : "md-down"} scrollable={true}
                       onHide={() => setShowCreate(false)}>
                    <div className="modal-header">
                        <h5 className="mb-0">Добавление объекта</h5>
                        <div className={"d-flex gap-3"}>
                            <button type="button" className="btn-link" onClick={() => setFullscreen(!fullscreen)}>
                                <FaExpandAlt/>
                            </button>
                            <button type="button" className="btn-close" onClick={() => setShowCreate(false)}></button>
                        </div>
                    </div>
                    <Form noValidate={true} validated={validated} onSubmit={handleSubmitCreate}>
                        {categories

                            ?
                            <>
                                <Modal.Body>
                                    {errorForm && <Alert variant={"danger"} dismissible={true}>{errorForm}</Alert>}
                                    <Form.Group as={Row} className="mb-3" controlId="category">
                                        <Form.Label column sm="2">Категория <span
                                            className={"text-danger"}>*</span></Form.Label>
                                        <Col sm="10">
                                            <Form.Select required={true} isInvalid={!categoryId}
                                                         onChange={(e) => setCategoryId(e.target.value)}>
                                                <option value={""}>-- Выберите --</option>
                                                {categories.map((category: any) => {
                                                    return (
                                                        <option key={category.id}
                                                                value={category.id}>{category.name}</option>
                                                    )
                                                })}
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">Поле обязательно для
                                                заполнения</Form.Control.Feedback>
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} className="mb-3" controlId="district">
                                        <Form.Label column sm="2">Район <span
                                            className={"text-danger"}>*</span></Form.Label>
                                        <Col sm="10">
                                            <Form.Control type="text" autoComplete={"off"}
                                                          value={district}
                                                          onChange={(e) => {
                                                              setDistrict(e.target.value)
                                                          }}
                                                          required={true}

                                            />
                                            <Form.Control.Feedback type="invalid">Поле обязательно для
                                                заполнения</Form.Control.Feedback>
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} className="mb-3" controlId="address">
                                        <Form.Label column sm="2">Адрес <span
                                            className={"text-danger"}>*</span></Form.Label>
                                        <Col sm="10">
                                            <Form.Control type="text" autoComplete={"off"}
                                                          value={address}
                                                          onChange={(e) => {
                                                              setAddress(e.target.value)
                                                          }}
                                                          required={true}
                                            />
                                            <Form.Control.Feedback type="invalid">Поле обязательно для
                                                заполнения</Form.Control.Feedback>
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} className="mb-3" controlId="name">
                                        <Form.Label column sm="2">Название <span
                                            className={"text-danger"}>*</span></Form.Label>
                                        <Col sm="10">
                                            <Form.Control type="text" autoComplete={"off"}
                                                          value={name}
                                                          onChange={(e) => {
                                                              setName(e.target.value)
                                                          }}
                                                          required={true}
                                            />
                                            <Form.Control.Feedback type="invalid">Поле обязательно для
                                                заполнения</Form.Control.Feedback>
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} className="mb-3" controlId="comment">
                                        <Form.Label column sm="2">Фотографии (до 10)</Form.Label>
                                        <Col sm="10">
                                            {errorFiles &&
                                                <Alert variant={"danger"} dismissible={true}>{errorFiles}</Alert>}
                                            <Form.Control type="file" multiple={true} accept={"image/*"}
                                                          onChange={selectFile}
                                            />
                                            <Form.Text className="text-muted ps-2">Можно выбрать несколько
                                                файлов</Form.Text>
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} className="mb-3" controlId="comment">
                                        <Form.Label column sm="2">Комментарий</Form.Label>
                                        <Col sm="10">
                                            <Form.Control as="textarea" autoComplete={"off"} rows={4}
                                                          value={comment}
                                                          onChange={(e) => {
                                                              setComment(e.target.value)
                                                          }}
                                            />
                                        </Col>
                                    </Form.Group>
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button variant="primary" type="submit">Добавить</Button>
                                    <Button variant="secondary" onClick={() => setShowCreate(false)}>Отмена</Button>
                                </Modal.Footer>
                            </>
                            :
                            <>
                                <Modal.Body>
                                    <Alert variant={"warning"}>
                                        <div className={"h6"}>Нет категорий для добавления объекта.</div>
                                        <div>Добавьте категорию в разделе "Категории".</div>
                                    </Alert>
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button variant="secondary" onClick={() => setShowCreate(false)}>Закрыть</Button>
                                </Modal.Footer>
                            </>
                        }
                    </Form>
                </Modal>
            )}

            {showView && <ObjectModalView objectId={objectId} visible={showView} setVisible={setShowView}
                                          refreshTable={fetchObjects}/>}

            {showEdite && <ObjectModalEdite objectId={objectId} visible={showEdite} setVisible={setShowEdite}
                                            refreshTable={fetchObjects} categories={categories}/>}

            {showDelete && <ObjectModalDelete objectId={objectId} visible={showDelete} setVisible={setShowDelete}
                                              refreshTable={fetchObjects}/>}
        </>
    );
};

export default UserObjects;